import Box from "@mui/material/Box";
import {useTheme} from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import {useReadOnly} from "crqlar-react-common/dist/utils/hooks/users/useReadOnly";
import {UIEvent, useEffect, useMemo, useRef, useState} from "react";
import {Outlet, useLocation} from "react-router-dom";
import {SettingsRecentRoutesProvider} from "../settings/search/SettingsRecentRoutes";
import {SettingsHeaderContext} from "./SettingsHeaderContext";
import SettingsSidebar from "./SettingsSidebar";

const SettingsLayout = () => {
    const readOnly = useReadOnly()
    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down('md'))
    const location = useLocation()
    const [hostElement, setHostElement] = useState<HTMLElement | null>(null)
    const [contentScrolled, setContentScrolled] = useState(false)
    const contentRef = useRef<HTMLDivElement>(null)
    const headerContext = useMemo(
        () => ({hostElement, isMobile, contentScrolled}),
        [contentScrolled, hostElement, isMobile]
    )
    const handleContentScroll = (event: UIEvent<HTMLDivElement>) => {
        setContentScrolled(event.currentTarget.scrollTop > 0)
    }

    useEffect(() => {
        if (contentRef.current) contentRef.current.scrollTop = 0
        setContentScrolled(false)
    }, [isMobile, location.pathname])

    return (
        <SettingsRecentRoutesProvider>
            <SettingsHeaderContext.Provider value={headerContext}>
                <Box
                    className="App"
                    sx={{
                        display: 'flex',
                        width: '100%',
                        height: 'calc(100dvh - var(--banner-offset, 0px))',
                        minHeight: 0,
                        overflow: 'hidden',
                        bgcolor: 'background.paper',
                    }}
                >
                    <Box sx={{display: {xs: 'none', md: 'block'}, height: '100%', flexShrink: 0}}>
                        <SettingsSidebar/>
                    </Box>

                    <Box
                        data-readonly-lock-scope=""
                        data-readonly-user={readOnly ? 'true' : undefined}
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            flex: 1,
                            minWidth: 0,
                            minHeight: 0,
                            '&[data-readonly-locked="true"]': {
                                '& .MuiInputBase-root, & .MuiAutocomplete-root, & .MuiSelect-select, & .MuiSwitch-root, & .MuiCheckbox-root, & .MuiRadio-root': {
                                    pointerEvents: 'none',
                                },
                                '& .MuiInputBase-root, & .MuiSwitch-root, & .MuiCheckbox-root, & .MuiRadio-root': {
                                    opacity: 0.6,
                                },
                            },
                            '&[data-readonly-user="true"] button:has(svg[data-testid="AddIcon"])': {
                                pointerEvents: 'none',
                                opacity: 0.5,
                            },
                        }}
                    >
                        {isMobile ? (
                            <>
                                <Box ref={setHostElement} sx={{width: '100%', flexShrink: 0}}/>

                                <Box
                                    ref={contentRef}
                                    onScroll={handleContentScroll}
                                    sx={{
                                        display: 'flex',
                                        flex: 1,
                                        minWidth: 0,
                                        minHeight: 0,
                                        overflow: 'auto',
                                        px: 1.5,
                                        boxSizing: 'border-box',
                                    }}
                                >
                                    <Outlet/>
                                </Box>
                            </>
                        ) : (
                            <Box
                                ref={contentRef}
                                onScroll={handleContentScroll}
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    flex: 1,
                                    minWidth: 0,
                                    minHeight: 0,
                                    overflow: 'auto',
                                }}
                            >
                                <Box
                                    ref={setHostElement}
                                    sx={{
                                        position: 'sticky',
                                        top: 0,
                                        zIndex: theme => theme.zIndex.appBar,
                                        width: '100%',
                                        flexShrink: 0,
                                    }}
                                />

                                <Box
                                    sx={{
                                        display: 'flex',
                                        minWidth: 0,
                                        px: 2,
                                        pt: 2,
                                        boxSizing: 'border-box',
                                    }}
                                >
                                    <Outlet/>
                                </Box>
                            </Box>
                        )}
                    </Box>
                </Box>
            </SettingsHeaderContext.Provider>
        </SettingsRecentRoutesProvider>
    )
}

export default SettingsLayout
